import { useState, MouseEvent, useEffect, useCallback } from "react";
import {
  Box,
  // Button,
  // ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Button,
  ButtonGroup,
} from "@mui/material";

import {
  Event,
  dateFnsLocalizer,
  View,
  ToolbarProps,
  NavigateAction,
} from "react-big-calendar";
import CalendarWrapper from "./CalendarWrapper";

import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addDays from "date-fns/addDays";
import subDays from "date-fns/subDays";

import "react-big-calendar/lib/css/react-big-calendar.css";

// Import color utility functions
import { hashCode, generateColor } from "../../utils/colorUtils"; // Adjust path as needed

import EventInfo from "./EventInfo";
import AddEventModal from "./AddEventModal";
import EventInfoModal from "./EventInfoModal";
import { AddTodoModal } from "./AddTodoModal";
import AddDatePickerEventModal from "./AddDatePickerEventModal";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import axios from "axios";
// import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';

/**
 * Gets the JWT token from cookies
 *
 * @returns {string} The JWT token
 */
const getAuthToken = () => {
  const cookies = document.cookie.split(";");
  let token = null;

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "_auth") {
      token = decodeURIComponent(value);
      break;
    }
  }

  return token;
};

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export interface ITodo {
  _id: string;
  title: string;
  color?: string;
}

export interface IEventInfo extends Event {
  _id: string;
  description: string;
  todoId?: string;
  userId: number; // User ID from User Object
  companyId: number; // CID from User Object
  email: string; // User ID from User Object
  companyName: string;
  roomId: number;
  attendees?: string[]; // Existing: for displaying attendees
  allDay?: boolean; // Existing: for event display logic
  title?: string; // Existing: for react-big-calendar event object
}

export interface EventFormData {
  description: string;
  todoId?: string;
  userId: number;
  companyId: number;
  email: string;
  companyName: string;
  roomId: number;
  attendees: string[]; // This is the target for EventFormData
}

export interface DatePickerEventFormData {
  description: string;
  todoId?: string;
  allDay: boolean;
  start?: Date;
  end?: Date;
  email: "";
  companyName: "";
  roomId: 0;
  attendees: []; // Ensure this is present and initialized
}

// Defining the type for the roomName prop
interface EventCalendarProps {
  roomName: string;
  roomid: string;
  isAllRoomsView?: boolean;
  locationId?: string;
  allRooms?: {
    room_id: string;
    name: string;
    lid: string;
    cap: number;
    credits_hourly: number;
  }[];
  eventsData?: IEventInfo[]; // Add prop to accept external events
  readOnly?: boolean; // Add prop for read-only mode
  isLoading?: boolean; // Add prop for loading state
  defaultViewProp?: View; // Add prop for default view
  subheaderText?: string; // New prop for custom subheader
  guestKey?: string; // Add prop for guest access key
}

export const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).substring(2);

const initialEventFormState: EventFormData = {
  description: "",
  todoId: undefined,
  userId: 0,
  companyId: 0,
  email: "",
  companyName: "",
  roomId: 0,
  attendees: [], // Initialize attendees here for EventFormData
};

const initialDatePickerEventFormData: DatePickerEventFormData = {
  description: "",
  todoId: undefined,
  allDay: false,
  start: undefined,
  end: undefined,
  email: "",
  companyName: "",
  roomId: 0,
  attendees: [], // Initialize attendees as an empty array
};

// Define an interface for CustomToolbar props that includes isAllRoomsView
interface CustomToolbarProps extends ToolbarProps {
  isAllRoomsView?: boolean;
}

// Custom Toolbar Component defined outside EventCalendar
const CustomToolbar = (props: CustomToolbarProps) => {
  // Destructure the toolbar props
  const { onNavigate, label, onView, view, isAllRoomsView = false } = props;

  // ... (rest of CustomToolbar implementation as before) ...

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <ButtonGroup
          variant='outlined'
          sx={{ mr: 2 }}
        >
          <Button onClick={() => onNavigate("TODAY")}>Today</Button>
          <Button onClick={() => onNavigate("PREV")}>Back</Button>
          <Button onClick={() => onNavigate("NEXT")}>Next</Button>
        </ButtonGroup>
        <Box sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>{label}</Box>
      </Box>

      <ButtonGroup variant='outlined'>
        {/* Day View - Always visible */}
        <Button
          onClick={() => onView("day")}
          variant={view === "day" ? "contained" : "outlined"}
        >
          Day
        </Button>

        {/* Week View - Always visible */}
        <Button
          onClick={() => onView("week")}
          variant={view === "week" ? "contained" : "outlined"}
        >
          Week
        </Button>

        {/* Month View - Visible only for single room view */}
        {!isAllRoomsView && (
          <Button
            onClick={() => onView("month")}
            variant={view === "month" ? "contained" : "outlined"}
          >
            Month
          </Button>
        )}

        {/* Agenda View - No longer used based on requirements */}
        {/*
        {!isAllRoomsView && (
          <Button
            onClick={() => onView("agenda")}
            variant={view === "agenda" ? "contained" : "outlined"}
          >
            Agenda
          </Button>
        )}
        */}
      </ButtonGroup>
    </Box>
  );
};

const EventCalendar: React.FC<EventCalendarProps> = ({
  roomName,
  roomid,
  isAllRoomsView = false,
  locationId = "",
  allRooms = [],
  eventsData,
  readOnly = false,
  isLoading = false,
  defaultViewProp = "day",
  subheaderText = "Book a meeting!",
  guestKey,
}) => {
  //extracts the roomName prop from the props object and uses it within the component.
  // API endpoints
  const baseEventsApi = process.env.REACT_APP_API_URL + "/api/get-events-list";
  const createEventApi = process.env.REACT_APP_API_URL + "/api/create-event";
  const deleteEventApi = process.env.REACT_APP_API_URL + "/api/delete-event";

  const authUser = useAuthUser<any>();
  // const isAuthenticated = useIsAuthenticated();
  const [openSlot, setOpenSlot] = useState(false);
  const [openDatepickerModal, setOpenDatepickerModal] = useState(false);
  const [openTodoModal, setOpenTodoModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | IEventInfo | null>(
    null
  );

  // Current date state to track calendar navigation
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  // Track the current view - Initialize with defaultViewProp
  const [currentView, setCurrentView] = useState<View>(defaultViewProp);

  const [eventInfoModal, setEventInfoModal] = useState(false);

  const [events, setEvents] = useState<IEventInfo[]>([]);
  const [todos, setTodos] = useState<ITodo[]>([]);

  // Effect to update internal events state when external data is provided
  useEffect(() => {
    if (eventsData && eventsData.length > 0) {
      const processedEvents = eventsData.map((event) => ({
        ...event,
        start: event.start ? new Date(event.start) : new Date(), // Handle potential string dates
        end: event.end ? new Date(event.end) : new Date(), // Handle potential string dates
      }));
      setEvents(processedEvents);
    }
  }, [eventsData]);

  // Function to fetch events for a specific room
  const fetchEventsForRoom = useCallback(
    async (roomId: string, date: Date) => {
      try {
        // Get the JWT token from cookies
        const token = getAuthToken();

        // Calculate start and end dates for the month
        const startDate = format(startOfMonth(date), "yyyy-MM-dd");
        const endDate = format(addDays(endOfMonth(date), 1), "yyyy-MM-dd");

        // Build the URL with query parameters
        const url = `${baseEventsApi}?roomId=${roomId}&limit=2000&startDate=${startDate}&endDate=${endDate}`;

        // Conditionally set headers
        const headers: Record<string, string> = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await axios.get(url, { headers });

        // Check if the response contains the expected property
        if (response && response.data) {
          // Handle the paginated response structure
          // The API returns { data: [...events], total, page, limit, totalPages }
          const eventsData = response.data.data || [];

          // Parse dates for each event
          return eventsData.map((event: IEventInfo) => {
            const start = event.start ? new Date(event.start) : new Date();
            const end = event.end ? new Date(event.end) : new Date();

            return {
              ...event,
              start,
              end,
            };
          });
        } else {
          console.error("Invalid response format:", response);
          return [];
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        return [];
      }
    },
    [baseEventsApi]
  );

  // Function to fetch events for all rooms in a location
  const fetchEventsForAllRooms = useCallback(
    async (date: Date) => {
      if (!isAllRoomsView || allRooms.length === 0) return;

      try {
        // Create an array of promises for fetching events for each room
        const promises = allRooms.map((room) =>
          fetchEventsForRoom(room.room_id, date)
        );

        // Wait for all promises to resolve
        const results = await Promise.all(promises);

        // Combine all events from all rooms
        const allEvents = results.flat();

        // Update the events state with the combined data
        setEvents(allEvents);
      } catch (error) {
        console.error("Error fetching events for all rooms:", error);
      }
    },
    [isAllRoomsView, allRooms, fetchEventsForRoom]
  );

  // Function to fetch events for a single room (reusing existing function)
  const fetchEventsForMonth = useCallback(
    async (date: Date) => {
      if (isAllRoomsView) return; // Skip for "All" view as it uses fetchEventsForAllRooms

      try {
        // Get the JWT token from cookies
        const token = getAuthToken();

        // Calculate start and end dates for the month
        const startDate = format(startOfMonth(date), "yyyy-MM-dd");
        const endDate = format(addDays(endOfMonth(date), 1), "yyyy-MM-dd");

        // Build the URL with query parameters
        const url = `${baseEventsApi}?roomId=${roomid}&limit=2000&startDate=${startDate}&endDate=${endDate}`;

        // Conditionally set headers
        const headers: Record<string, string> = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await axios.get(url, { headers });

        // Check if the response contains the expected property
        if (response && response.data) {
          // Handle the paginated response structure
          // The API returns { data: [...events], total, page, limit, totalPages }
          const eventsData = response.data.data || [];

          // Parse dates for each event
          const fetchedEvents: IEventInfo[] = eventsData.map(
            (event: IEventInfo) => {
              const start = event.start ? new Date(event.start) : new Date();
              const end = event.end ? new Date(event.end) : new Date();

              return {
                ...event,
                start,
                end,
              };
            }
          );

          // Update the events state with the fetched data
          setEvents(fetchedEvents);
        } else {
          console.error("Invalid response format:", response);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },
    [baseEventsApi, roomid, isAllRoomsView]
  );

  // Handle calendar navigation
  const handleNavigate = (action: NavigateAction | Date) => {
    // If action is a Date object, the library likely calculated the target date for us.
    if (action instanceof Date) {
      // Directly set the state to the date provided by the library
      setCurrentDate(action);
      // Let useEffect handle fetching based on currentDate change
    } else {
      // Otherwise handle the string navigation actions ("PREV", "NEXT", "TODAY")
      let newDate = currentDate;

      switch (action) {
        case "TODAY":
          newDate = new Date();
          break;
        case "PREV":
          // Navigate based on the current view
          if (currentView === "month") {
            newDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() - 1,
              currentDate.getDate()
            );
          } else if (currentView === "week") {
            newDate = subDays(currentDate, 7);
          } else {
            // Assuming 'day' or 'agenda'
            newDate = subDays(currentDate, 1);
          }
          break;
        case "NEXT":
          // Navigate based on the current view
          if (currentView === "month") {
            newDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() + 1,
              currentDate.getDate()
            );
          } else if (currentView === "week") {
            newDate = addDays(currentDate, 7);
          } else {
            // Assuming 'day' or 'agenda'
            newDate = addDays(currentDate, 1);
          }
          break;
        default:
          return;
      }

      setCurrentDate(newDate);
      // Let useEffect handle fetching based on currentDate change
    }
  };

  // Initial fetch of events logic refined
  useEffect(() => {
    // If external data is provided, don't fetch internally
    if (eventsData) {
      return;
    }

    if (isAllRoomsView) {
      // Fetch for all rooms, only depends on isAllRoomsView, allRooms, fetchEventsForRoom, currentDate
      fetchEventsForAllRooms(currentDate);
    } else if (roomid) {
      // Fetch for a single room, depends on roomid, isAllRoomsView, baseEventsApi, currentDate
      fetchEventsForMonth(currentDate);
    }
    // Optional: Clear events if no room is selected and not in all rooms view
    // setEvents([]);
  }, [
    // Add dependencies based on ESLint warning and analysis
    eventsData, // External data dependency
    isAllRoomsView,
    currentDate,
    allRooms, // Needed for fetchEventsForAllRooms
    fetchEventsForRoom, // Needed for fetchEventsForAllRooms
    fetchEventsForAllRooms, // Function dependency
    roomid, // Needed for fetchEventsForMonth
    baseEventsApi, // Needed for fetchEventsForMonth
    fetchEventsForMonth, // Function dependency
  ]);

  const authUserId = authUser?.uid;
  const authCompanyId = Number(authUser?.companyId) || 0;
  const [eventFormData, setEventFormData] = useState<EventFormData>(() => ({
    ...initialEventFormState,
    companyId: authCompanyId,
  }));

  useEffect(() => {
    // Remember selections only for the current account and company.
    setEventFormData({
      ...initialEventFormState,
      companyId: authCompanyId,
    });
  }, [authUserId, authCompanyId]);

  const [datePickerEventFormData, setDatePickerEventFormData] =
    useState<DatePickerEventFormData>(initialDatePickerEventFormData);

  const handleSelectSlot = (event: Event) => {
    // Check if user can add events before opening the modal
    if (!authUser && !guestKey) {
      alert("You must be logged in or have a guest key to book a meeting.");
      return;
    }

    if (authUser && (authUser.companyIsActive === 0 || authUser.companyIsActive === false)) {
      alert("Your company is inactive. You cannot book meetings.");
      return;
    }

    setOpenSlot(true);
    setCurrentEvent(event);
  };

  const handleSelectEvent = (event: IEventInfo) => {
    setCurrentEvent(event);
    setEventInfoModal(true);
  };

  const handleClose = () => {
    setEventFormData((prevState) => ({
      ...initialEventFormState,
      companyId: prevState.companyId,
    }));
    setOpenSlot(false);
  };

  const handleDatePickerClose = () => {
    setDatePickerEventFormData(initialDatePickerEventFormData);
    setOpenDatepickerModal(false);
  };

  /**
   * Checks if a time slot is available for booking
   *
   * @param {Event | IEventInfo} newEvent - The event to check availability for
   * @param {IEventInfo[]} events - Array of existing events to check against
   * @param {number} [targetRoomId] - Optional room ID to filter events by.
   *                                  If provided, only checks for conflicts within the same room.
   *                                  This is crucial for "All Rooms" view to avoid false conflicts.
   * @returns {boolean} - Returns true if there are overlapping events (slot unavailable)
   */
  const isTimeSlotAvailable = (
    newEvent: Event | IEventInfo,
    events: IEventInfo[],
    targetRoomId?: number
  ) => {
    // Check if newEvent is null or undefined
    if (!newEvent) {
      return false; // Return false if newEvent is null or undefined
    }

    // Check if any event in the events array is null or undefined
    if (events.some((event) => !event)) {
      return false; // Return false if any event is null or undefined
    }

    if (newEvent.start && newEvent.start < new Date()) {
      return true;
    }

    // Filter events to only check those in the same room if targetRoomId is provided
    const eventsToCheck = targetRoomId
      ? events.filter((event) => event.roomId === targetRoomId)
      : events;

    // Check if the time slot overlaps with any existing event in the same room
    return eventsToCheck.some(
      (event) => newEvent.start! < event.end! && newEvent.end! > event.start!
    );
  };

  // Event is added here - work on adding logic
  const onAddEvent = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Check if user can add events
    if (!authUser && !guestKey) {
      alert("You must be logged in or have a guest key to book a meeting.");
      return;
    }

    if (authUser && (authUser.companyIsActive === 0 || authUser.companyIsActive === false)) {
      alert("Your company is inactive. You cannot book meetings.");
      return;
    }

    // For guest users, ensure they provide an email
    if (guestKey && !authUser && !eventFormData.email) {
      alert("Please provide an email address to book as a guest.");
      return;
    }

    const requiresCompanySelection = authUser?.typeId >= 3;
    if (
      requiresCompanySelection &&
      (!Number.isInteger(eventFormData.companyId) || eventFormData.companyId <= 0)
    ) {
      alert("Please select a company before booking.");
      return;
    }

    // Employees/admins must submit the company shown in the booking form.
    const companyIdToUse = authUser
      ? requiresCompanySelection
        ? eventFormData.companyId
        : authCompanyId
      : 0; // Default for guest users

    // For "All" rooms view, we need to check if the event has a roomId
    // If roomId is 'all_rooms', we need to get the resource ID from the current event
    const roomIdToUse =
      isAllRoomsView && currentEvent && "resourceId" in currentEvent
        ? Number(currentEvent.resourceId) || 0
        : Number(roomid) || 0;

    const data: IEventInfo = {
      ...eventFormData,
      _id: generateId(),
      start: currentEvent?.start,
      end: currentEvent?.end,
      userId: authUser ? authUser.uid : 0, // Default for guest users
      email: authUser ? authUser.email : eventFormData.email, // Use guest email if not authenticated
      companyId: companyIdToUse,
      companyName: authUser ? "" : eventFormData.companyName || "Guest", // Default for guests
      roomId: roomIdToUse,
    };

    // Get the JWT token from cookies
    const token = getAuthToken();

    // Prepare headers - include guest key if available
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (guestKey) {
      headers["X-Guest-Key"] = guestKey;
    }

    axios
      .post(
        createEventApi,
        {
          _id: data._id,
          start: data.start,
          end: data.end,
          userId: data.userId,
          companyId: data.companyId,
          roomId: data.roomId,
          description: data.description,
          todoId: data.todoId,
          attendees: data.attendees,
          email: data.email, // Include email for guest bookings
          companyName: data.companyName,
        },
        {
          headers: headers,
        }
      )
      .then(function (response) {
        // Refresh the events for the current month after adding a new event
        if (isAllRoomsView) {
          fetchEventsForAllRooms(currentDate);
        } else {
          fetchEventsForMonth(currentDate);
        }
        handleClose();
      })
      .catch(function (error) {
        alert(
          error.response?.data?.error ||
            error.message ||
            "Failed to create event"
        );
      });
  };

  const onAddEventFromDatePicker = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Check if user can add events
    if (!authUser && !guestKey) {
      alert("You must be logged in or have a guest key to book a meeting.");
      return;
    }

    if (authUser && (authUser.companyIsActive === 0 || authUser.companyIsActive === false)) {
      alert("Your company is inactive. You cannot book meetings.");
      return;
    }

    // For guest users, ensure they provide an email
    if (guestKey && !authUser && !datePickerEventFormData.email) {
      alert("Please provide an email address to book as a guest.");
      return;
    }

    const addHours = (date: Date | undefined, hours: number) => {
      return date ? date.setHours(date.getHours() + hours) : undefined;
    };

    const setMinToZero = (date: any) => {
      date.setSeconds(0);

      return date;
    };

    // When in "All" rooms view, we need to select which room to add the event to
    // For simplicity, we'll use the first room in the list if available
    const roomIdToUse =
      isAllRoomsView && allRooms && allRooms.length > 0
        ? Number(allRooms[0].room_id) || 0
        : Number(roomid) || 0;

    const data: IEventInfo = {
      ...datePickerEventFormData,
      _id: generateId(),
      start: setMinToZero(datePickerEventFormData.start),
      end: datePickerEventFormData.allDay
        ? addHours(datePickerEventFormData.start, 12)
        : setMinToZero(datePickerEventFormData.end),
      userId: authUser ? authUser.uid : 0, // Default for guest users
      companyId: authUser ? authUser.companyId || 0 : 0, // Default for guest users
      email: authUser ? authUser.email : datePickerEventFormData.email, // Use guest email if not authenticated
      companyName: authUser
        ? ""
        : datePickerEventFormData.companyName || "Guest", // Default for guests
      roomId: roomIdToUse,
    };

    // Get the JWT token from cookies
    const token = getAuthToken();

    // Prepare headers - include guest key if available
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (guestKey) {
      headers["X-Guest-Key"] = guestKey;
    }

    axios
      .post(
        createEventApi,
        {
          _id: data._id,
          start: data.start,
          end: data.end,
          userId: data.userId,
          companyId: data.companyId,
          roomId: data.roomId,
          description: data.description,
          todoId: data.todoId,
          attendees: data.attendees,
          email: data.email, // Include email for guest bookings
          companyName: data.companyName,
        },
        {
          headers: headers,
        }
      )
      .then(function (response) {
        // Refresh the events for the current month after adding a new event
        if (isAllRoomsView) {
          fetchEventsForAllRooms(currentDate);
        } else {
          fetchEventsForMonth(currentDate);
        }
        setDatePickerEventFormData(initialDatePickerEventFormData);
        handleDatePickerClose();
      })
      .catch(function (error) {
        alert(
          error.response?.data?.error ||
            error.message ||
            "Failed to create event"
        );
      });
  };

  const onDeleteEvent = () => {
    // Get the JWT token from cookies
    const token = getAuthToken();

    axios
      .post(
        deleteEventApi,
        {
          //fname: data.get('')
          _id: (currentEvent as IEventInfo)._id,
          companyId: (currentEvent as IEventInfo).companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(function (response) {
        // Refresh the events for the current month after deleting an event
        if (isAllRoomsView) {
          fetchEventsForAllRooms(currentDate);
        } else {
          fetchEventsForMonth(currentDate);
        }
        setEventInfoModal(false);
      })
      .catch(function (error) {
        alert(error.response.data.error);
      });
  };

  // Create room resource for the "All" view
  const resources = isAllRoomsView
    ? allRooms.map((room) => ({
        id: room.room_id.toString(),
        title: room.name,
      }))
    : undefined;

  // For the "All" view, we need to add a roomId resource to each event
  const eventsWithResource = isAllRoomsView
    ? events.map((event) => ({
        ...event,
        resourceId: event.roomId ? event.roomId.toString() : "",
      }))
    : events;

  // Handle view change
  const handleViewChange = (newView: View) => {
    setCurrentView(newView);
    // Note: Fetching is handled by useEffect based on currentDate,
    // view change itself doesn't necessarily require immediate refetch unless date logic changes
  };

  // Correctly typed wrapper function for the toolbar
  const renderToolbar: React.FC<ToolbarProps> = (toolbarProps) => (
    <CustomToolbar
      {...toolbarProps}
      isAllRoomsView={isAllRoomsView}
    />
  );

  return (
    <Box
      mb={2}
      component='main'
      sx={{
        flexGrow: 1,
        pb: 8,
      }}
    >
      <Container maxWidth={false}>
        <Card
          sx={{
            background: "#00000000",
            border: "none",
            boxShadow: "0px 0px 16px 8px rgba(203, 224, 15, 0.2)",
          }}
        >
          <CardHeader
            title={roomName}
            subheader={subheaderText}
          />
          <Divider />
          <CardContent>
            {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <ButtonGroup>
                <Button
                  onClick={() => setOpenDatepickerModal(true)}
                  size="medium"
                  variant="contained"
                >
                  New Meeting
                </Button>
              </ButtonGroup>
            </Box> */}
            {/* <Divider style={{ margin: 10 }} /> */}
            <AddEventModal
              open={openSlot}
              handleClose={handleClose}
              eventFormData={eventFormData || initialEventFormState}
              setEventFormData={setEventFormData}
              onAddEvent={onAddEvent}
              todos={todos}
              timeSlotAvailable={
                !isTimeSlotAvailable(
                  currentEvent!,
                  events,
                  isAllRoomsView && currentEvent && "resourceId" in currentEvent
                    ? Number(currentEvent.resourceId) || 0
                    : Number(roomid) || 0
                )
              }
              start={currentEvent?.start?.toLocaleTimeString()!}
              end={currentEvent?.end?.toLocaleTimeString()!}
              guestKey={guestKey}
            />
            <AddDatePickerEventModal
              open={openDatepickerModal}
              handleClose={handleDatePickerClose}
              datePickerEventFormData={
                datePickerEventFormData || initialDatePickerEventFormData
              }
              setDatePickerEventFormData={setDatePickerEventFormData}
              onAddEvent={onAddEventFromDatePicker}
              todos={todos}
              timeSlotAvailable={
                !isTimeSlotAvailable(
                  currentEvent!,
                  events,
                  isAllRoomsView && allRooms && allRooms.length > 0
                    ? Number(allRooms[0].room_id) || 0
                    : Number(roomid) || 0
                )
              }
            />
            <EventInfoModal
              open={eventInfoModal}
              handleClose={() => setEventInfoModal(false)}
              onDeleteEvent={onDeleteEvent}
              currentEvent={currentEvent as IEventInfo}
            />
            <AddTodoModal
              open={openTodoModal}
              handleClose={() => setOpenTodoModal(false)}
              todos={todos}
              setTodos={setTodos}
            />
            <CalendarWrapper
              localizer={localizer}
              events={eventsWithResource}
              onSelectEvent={readOnly ? undefined : handleSelectEvent}
              onSelectSlot={readOnly ? undefined : handleSelectSlot}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onDrillDown={readOnly ? undefined : undefined}
              onSelecting={readOnly ? () => false : undefined}
              date={currentDate}
              selectable={!readOnly}
              dayLayoutAlgorithm={"no-overlap"}
              startAccessor='start'
              components={{
                event: EventInfo,
                toolbar: renderToolbar,
              }}
              step={15}
              min={new Date(0, 0, 0, 9, 0, 0)}
              max={new Date(0, 0, 0, 21, 0, 0)}
              endAccessor='end'
              defaultView={defaultViewProp}
              views={{
                month: !isAllRoomsView, // Show month view only for single rooms
                week: true,
                day: true,
                agenda: false, // Remove agenda view
              }}
              resources={resources}
              resourceIdAccessor='id'
              resourceTitleAccessor='title'
              resourceAccessor='resourceId'
              eventPropGetter={(event) => {
                const hasTodo = todos.find((todo) => todo._id === event.todoId);
                // Generate color based on companyName hash if no specific todo color exists
                const companyNameHash = event.companyName
                  ? hashCode(event.companyName)
                  : 0;
                const defaultColor = generateColor(companyNameHash);

                return {
                  style: {
                    backgroundColor: hasTodo ? hasTodo.color : defaultColor,
                    borderColor: hasTodo ? hasTodo.color : defaultColor,
                    // color: "black", // Keeping the text color logic separate (handled in EventInfo)
                  },
                };
              }}
              style={{
                height: 900,
              }}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default EventCalendar;
